import moment from 'moment';
import { formatDateStr } from '@intelligence/v3/utils/date';

import type { ITrendDataPoint } from '../types';

/**
 * @date 2025-10-05
 * @description 获取月份第一个周的星期天
 * @author lv.chen
 * @param monthDate 月份中的任意日期
 * @returns 该月份第一个周的星期天日期，如果不存在则返回null
 * @note 周归属定义：只要周的开始日期（星期天）在当月内即可，周的结束日期允许跨到下个月
 * @note 使用前必须确保moment已配置为以星期天为周的开始（dow: 0）
 */
const getFirstCompleteSundayOfMonth = (monthDate: string): string | null => {
  const monthM = moment(monthDate);
  const monthStart = monthM.clone().startOf('month');
  const monthEnd = monthM.clone().endOf('month');
  const month = monthM.month();

  // 从月初开始，找到第一个星期天
  const currentSunday = monthStart.clone().day(0);

  // 如果这个星期天在上个月，则移到下一周的星期天
  if (currentSunday.isBefore(monthStart)) {
    currentSunday.add(7, 'days');
  }

  // 检查这个星期天是否在当月内
  if (currentSunday.month() === month && currentSunday.isSameOrBefore(monthEnd)) {
    return currentSunday.format('YYYY-MM-DD');
  }

  // 如果没有找到，返回null
  return null;
};

/**
 * @date 2025-10-05
 * @description 获取月份最后一个周的星期天
 * @author lv.chen
 * @param monthDate 月份中的任意日期
 * @returns 该月份最后一个周的星期天日期，如果不存在则返回null
 * @note 周归属定义：只要周的开始日期（星期天）在当月内即可，周的结束日期允许跨到下个月
 * @note 使用前必须确保moment已配置为以星期天为周的开始（dow: 0）
 */
const getLastCompleteSundayOfMonth = (monthDate: string): string | null => {
  const monthM = moment(monthDate);
  const monthStart = monthM.clone().startOf('month');
  const monthEnd = monthM.clone().endOf('month');

  // 从月末开始，往前找到最后一个星期天
  const currentDay = monthEnd.clone();

  while (currentDay.isSameOrAfter(monthStart)) {
    if (currentDay.day() === 0) {
      // 找到星期天
      return currentDay.format('YYYY-MM-DD');
    }
    currentDay.subtract(1, 'day');
  }

  // 如果没有找到，返回null
  return null;
};

/**
 * @date 2025-10-05
 * @description 生成从起始日期到结束日期之间所有周的星期天日期列表
 * @author lv.chen
 * @param startSunday 起始周的星期天日期
 * @param endSunday 结束周的星期天日期
 * @returns 所有周的星期天日期列表（升序排列）
 * @note 使用前必须确保moment已配置为以星期天为周的开始（dow: 0）
 */
const generateWeekSundays = (startSunday: string, endSunday: string): string[] => {
  const sundays: string[] = [];
  const start = moment(startSunday);
  const end = moment(endSunday);

  const current = start.clone();

  while (current.isSameOrBefore(end)) {
    sundays.push(current.format('YYYY-MM-DD'));
    current.add(7, 'days');
  }

  return sundays;
};

/**
 * @date 2025-10-05
 * @description 填充周粒度趋势数据，补充缺失周的数据
 * @author lv.chen
 * @param trends 接口返回的原始趋势数据
 * @returns 填充后的完整趋势数据，缺失周的PV值为0
 * @note 使用前必须确保moment已配置为以星期天为周的开始（dow: 0）
 */
export const fillWeeklyTrends = (trends?: IQuarterReportPVHistory[] | null): IQuarterReportPVHistory[] => {
  if (!trends || trends.length === 0) {
    return [];
  }

  // 提取所有日期并排序
  const dates = trends.map((item) => item?.date).filter(Boolean);
  if (dates.length === 0) {
    return [];
  }

  dates.sort((a, b) => {
    const dateA = moment(a);
    const dateB = moment(b);
    return dateA.isBefore(dateB) ? -1 : 1;
  });

  const minDate = dates[0];
  const maxDate = dates[dates.length - 1];

  // 获取minDate所在月份的第一个完整周的星期天
  const startSunday = getFirstCompleteSundayOfMonth(minDate);
  // 获取maxDate所在月份的最后一个完整周的星期天
  const endSunday = getLastCompleteSundayOfMonth(maxDate);

  if (!startSunday || !endSunday) {
    // 如果无法找到完整周，返回原始数据
    return trends;
  }

  // 生成所有周的星期天日期列表
  const allSundays = generateWeekSundays(startSunday, endSunday);

  // 创建日期到PV值的映射
  const dateToValueMap: Record<string, number | null> = {};
  trends.forEach((item) => {
    if (item.date) {
      dateToValueMap[item.date] = item.pv;
    }
  });

  // 填充完整数据
  const filledTrends: IQuarterReportPVHistory[] = allSundays.map((sunday) => {
    return {
      date: sunday,
      pv: dateToValueMap[sunday] !== undefined ? dateToValueMap[sunday] : 0,
    };
  });

  return filledTrends;
};

/**
 * @date 2025-10-05
 * @description 计算每个月的第一个周的日期映射表
 * @param chartData 折线图数据
 * @author lv.chen
 * @returns 月份到第一周日期的映射，格式为 { "2025-01": "2025-01-05", "2025-02": "2025-02-02", ... }
 */
export const getMonthFirstWeekMap = (chartData?: ITrendDataPoint[] | null): IRecord<string> => {
  if (!Array.isArray(chartData) || chartData.length === 0) {
    return {};
  }

  const map: IRecord<string> = {};

  chartData.forEach((dataPoint) => {
    const date = dataPoint?.date;
    if (!date) return;

    // 提取月份标识（YYYY-MM格式）
    const monthKey = formatDateStr(date, 'YYYY-MM');

    // 如果该月份首次出现，记录该数据点的日期作为该月的第一周日期
    if (!map[monthKey]) {
      map[monthKey] = date;
    }
  });

  return map;
};
